import React, { useState } from 'react';
import Calculator from '../components/Calculator';
import Input from '../components/Input';
import { Shield, DollarSign, Percent, Calendar, Medal, Award, Info, CreditCard, TrendingDown } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Helmet } from 'react-helmet-async';
import SEOHead from '../components/SEOHead';
import { 
  calculateMilitaryLoan, 
  formatCurrency, 
  formatPercentage,
  MilitaryLoanInputs,
  MilitaryLoanResult
} from '../utils/militaryVeteranLoanCalculator';

const MilitaryVeteranLoanCalculator = () => {
  // State for inputs
  const [vehiclePrice, setVehiclePrice] = useState('');
  const [downPayment, setDownPayment] = useState('0');
  const [interestRate, setInterestRate] = useState('');
  const [loanTerm, setLoanTerm] = useState('');
  const [serviceStatus, setServiceStatus] = useState('active');
  const [serviceLength, setServiceLength] = useState('0');
  const [isInjuryPension, setIsInjuryPension] = useState(false);
  const [balloonPayment, setBalloonPayment] = useState('0');
  const [applicationFee, setApplicationFee] = useState('395');
  const [annualFee, setAnnualFee] = useState('120');
  
  // State for calculation result
  const [result, setResult] = useState<MilitaryLoanResult | null>(null);

  // Calculate loan when button is clicked
  const calculateLoan = () => {
    const inputs: MilitaryLoanInputs = {
      vehiclePrice: parseFloat(vehiclePrice) || 0,
      downPayment: parseFloat(downPayment) || 0,
      loanTerm: parseFloat(loanTerm) * 12 || 0, // Convert years to months
      interestRate: parseFloat(interestRate) || 0,
      serviceStatus,
      serviceLength: parseFloat(serviceLength) || 0,
      isInjuryPension,
      balloonPayment: parseFloat(balloonPayment) || 0,
      applicationFee: parseFloat(applicationFee) || 0,
      annualFee: parseFloat(annualFee) || 0
    };

    if (inputs.vehiclePrice > 0 && inputs.loanTerm > 0 && inputs.interestRate > 0) {
      const result = calculateMilitaryLoan(inputs);
      setResult(result);
    }
  };

  // Generate data for breakdown chart
  const getPaymentBreakdownData = () => {
    if (!result) return [];
    
    return [
      { name: 'Principal', value: result.loanAmount },
      { name: 'Interest', value: result.totalInterest },
      { name: 'Fees', value: result.totalFees }
    ];
  };

  // Generate data for amortization chart
  const getAmortizationChartData = () => {
    if (!result) return [];
    
    const yearlyData = [];
    const years = Math.ceil(result.amortizationSchedule.length / 12);
    
    for (let year = 1; year <= years; year++) {
      const yearStart = (year - 1) * 12;
      const yearEnd = Math.min(year * 12, result.amortizationSchedule.length) - 1;
      const startBalance = year === 1 
        ? result.loanAmount 
        : result.amortizationSchedule[yearStart - 1].balance;
      const endBalance = result.amortizationSchedule[yearEnd].balance;
      
      yearlyData.push({
        name: `Year ${year}`,
        principal: startBalance - endBalance,
        interest: result.amortizationSchedule
          .slice(yearStart, yearEnd + 1)
          .reduce((sum, month) => sum + month.interest, 0),
        balance: endBalance
      });
    }
    
    return yearlyData;
  };

  // Calculate the discount summary for display
  const getDiscountSummary = () => {
    if (!result) return null;

    const standardPayment = calculateStandardPayment();
    const monthlySavings = standardPayment - result.monthlyPayment;
    const totalSavings = monthlySavings * result.amortizationSchedule.length;

    return {
      standardPayment,
      monthlySavings,
      totalSavings,
      rateReduction: result.rateDiscount,
      feeSavings: result.feeDiscount
    };
  };

  // Calculate what payment would be without military/veteran discounts
  const calculateStandardPayment = () => {
    if (!result) return 0;

    const principal = result.loanAmount;
    const rate = result.originalRate / 100 / 12; // Monthly interest rate without discounts
    const term = result.amortizationSchedule.length;
    const balloon = parseFloat(balloonPayment) || 0;

    if (rate === 0) return (principal - balloon) / term;

    return (
      (principal * (rate * Math.pow(1 + rate, term))) / 
      (Math.pow(1 + rate, term) - 1)
    ) - (
      (balloon * rate) / 
      (Math.pow(1 + rate, term) - 1)
    );
  };

  // Get colors for charts
  const COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b'];

  return (
    <>
      <SEOHead
        title="ADF & Veteran Car Loan Calculator | Military Auto Finance Australia"
        description="Australian Defence Force (ADF) & Veterans car loan calculator with special military discounts. Compare service benefits, rate reductions, and fee waivers for your auto loan."
        keywords="adf car loan, veteran car loan australia, military vehicle finance, defence force auto loan, dva loan benefits, veteran loan calculator, adf finance benefits"
      />
      <Helmet>
        <title>ADF & Veteran Car Loan Calculator | Military Auto Finance Australia</title>
        <meta name="description" content="Australian Defence Force (ADF) & Veterans car loan calculator with special military discounts. Compare service benefits, rate reductions, and fee waivers for your auto loan." />
        <meta name="keywords" content="adf car loan, veteran car loan australia, military vehicle finance, defence force auto loan, dva loan benefits, veteran loan calculator, adf finance benefits" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FinancialProduct",
              "name": "Military & Veteran Auto Loan Calculator Australia",
              "description": "Calculate car loan repayments with ADF & veteran benefits, including service-based discounts on rates and fees for Australian Defence Force members and veterans.",
              "category": "LoanOrCredit",
              "feesAndCommissionsSpecification": "Reduced fees for ADF members and veterans",
              "areaServed": {
                "@type": "Country",
                "name": "Australia"
              },
              "interactivityType": "active",
              "financialProductCategory": "CreditCard"
            }
          `}
        </script>
      </Helmet>
      <Calculator
        title="ADF & Veteran Car Loan Calculator"
        description="Calculate your car loan repayments with special Australian Defence Force & Veteran benefits and discounts."
        icon={Shield}
        result={result ? `Monthly Repayment: ${formatCurrency(result.monthlyPayment)}` : undefined}
      >
        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-md mb-6">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
              <p className="text-sm text-blue-700">
                This calculator is specifically designed for Australian Defence Force members, veterans, and reservists.
                It includes special military rate discounts, fee waivers, and other benefits based on your service status.
              </p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Input
              label="Vehicle Price"
              value={vehiclePrice}
              onChange={setVehiclePrice}
              unit="$"
              placeholder="e.g. 30000"
            />
            <Input
              label="Down Payment"
              value={downPayment}
              onChange={setDownPayment}
              unit="$"
              placeholder="e.g. 5000"
            />
            <Input
              label="Standard Interest Rate"
              value={interestRate}
              onChange={setInterestRate}
              unit="%"
              placeholder="e.g. 5.25"
            />
            <Input
              label="Loan Term"
              value={loanTerm}
              onChange={setLoanTerm}
              unit="years"
              placeholder="e.g. 5"
            />
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Service Status
              </label>
              <select
                value={serviceStatus}
                onChange={(e) => setServiceStatus(e.target.value)}
                className="block w-full px-4 py-2 rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="active">Active ADF Service Member</option>
                <option value="veteran">Veteran</option>
                <option value="reserve">Reservist</option>
                <option value="civilian">Civilian (No Military Service)</option>
              </select>
            </div>
            
            <Input
              label="Years of Service"
              value={serviceLength}
              onChange={setServiceLength}
              unit="years"
              placeholder="e.g. 8"
            />
            
            <div className="mb-4 col-span-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isInjuryPension"
                  checked={isInjuryPension}
                  onChange={(e) => setIsInjuryPension(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isInjuryPension" className="ml-2 block text-sm text-gray-700">
                  Receiving DVA disability pension or payment
                </label>
              </div>
            </div>
            
            <div className="col-span-2">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Advanced Options</h3>
            </div>
            
            <Input
              label="Balloon Payment (Optional)"
              value={balloonPayment}
              onChange={setBalloonPayment}
              unit="$"
              placeholder="e.g. 5000"
            />
            
            <Input
              label="Application Fee"
              value={applicationFee}
              onChange={setApplicationFee}
              unit="$"
              placeholder="e.g. 395"
            />
            
            <Input
              label="Annual Fee"
              value={annualFee}
              onChange={setAnnualFee}
              unit="$"
              placeholder="e.g. 120"
            />
          </div>

          <button
            onClick={calculateLoan}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition"
          >
            Calculate ADF/Veteran Loan
          </button>

          {result !== null && (
            <div className="mt-6 space-y-6">
              <div className="p-4 bg-blue-50 rounded-md">
                <h3 className="text-lg font-semibold text-blue-900 mb-4">Loan Summary with Military Benefits</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="p-4 bg-white rounded-md shadow-sm">
                    <p className="text-sm text-gray-600">Monthly Repayment</p>
                    <p className="text-xl font-bold text-blue-800">{formatCurrency(result.monthlyPayment)}</p>
                  </div>
                  <div className="p-4 bg-white rounded-md shadow-sm">
                    <p className="text-sm text-gray-600">Total Interest</p>
                    <p className="text-xl font-bold text-blue-800">{formatCurrency(result.totalInterest)}</p>
                  </div>
                  <div className="p-4 bg-white rounded-md shadow-sm">
                    <p className="text-sm text-gray-600">Total Loan Cost</p>
                    <p className="text-xl font-bold text-blue-800">{formatCurrency(result.totalCost)}</p>
                  </div>
                </div>
                
                {/* Rate and Discount Information */}
                <div className="bg-white p-4 rounded-md shadow-sm mb-4">
                  <h4 className="font-semibold text-gray-800 mb-2">Your Military Discounts & Benefits</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center">
                      <Medal className="h-5 w-5 text-blue-600 mr-2" />
                      <div>
                        <p className="text-sm text-gray-600">Standard Rate</p>
                        <p className="text-md font-semibold text-gray-800">{formatPercentage(result.originalRate)}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Award className="h-5 w-5 text-green-600 mr-2" />
                      <div>
                        <p className="text-sm text-gray-600">Your Discounted Rate</p>
                        <p className="text-md font-semibold text-green-700">{formatPercentage(result.discountedRate)}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <TrendingDown className="h-5 w-5 text-green-600 mr-2" />
                      <div>
                        <p className="text-sm text-gray-600">Rate Reduction</p>
                        <p className="text-md font-semibold text-green-700">-{formatPercentage(result.rateDiscount)}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <DollarSign className="h-5 w-5 text-green-600 mr-2" />
                      <div>
                        <p className="text-sm text-gray-600">Fee Savings</p>
                        <p className="text-md font-semibold text-green-700">{formatCurrency(result.feeDiscount)}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Eligible Benefits */}
                {result.eligibleBenefits.length > 0 && (
                  <div className="bg-white p-4 rounded-md shadow-sm mb-4">
                    <h4 className="font-semibold text-gray-800 mb-2">Your Eligible Benefits</h4>
                    <ul className="space-y-1 ml-6 list-disc text-sm text-gray-700">
                      {result.eligibleBenefits.map((benefit, index) => (
                        <li key={index}>{benefit}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Comparison Rate */}
                <div className="mt-4 flex justify-between items-center bg-gray-50 p-3 rounded-md">
                  <div className="flex items-center">
                    <Percent className="h-4 w-4 text-blue-600 mr-2" />
                    <span className="text-sm">Comparison Rate:</span>
                  </div>
                  <span className="text-sm font-semibold">{result.comparisonRate.toFixed(2)}% p.a.</span>
                </div>
                
                {/* Disclaimer */}
                <p className="text-xs text-gray-500 mt-2">
                  * Comparison rates calculated based on a loan of ${result.loanAmount.toFixed(0)} over a term of {(result.amortizationSchedule.length / 12).toFixed(0)} years. Fees and charges included.
                </p>
              </div>

              {/* Savings Analysis */}
              {result && getDiscountSummary() && (
                <div className="p-4 bg-blue-50 rounded-md">
                  <h3 className="text-lg font-semibold text-blue-900 mb-4">Your Service Savings</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="p-4 bg-white rounded-md shadow-sm">
                      <p className="text-sm text-gray-600">Standard Monthly Payment</p>
                      <p className="text-xl font-bold text-gray-800">{formatCurrency(getDiscountSummary()!.standardPayment)}</p>
                    </div>
                    <div className="p-4 bg-white rounded-md shadow-sm">
                      <p className="text-sm text-gray-600">Your Monthly Savings</p>
                      <p className="text-xl font-bold text-green-600">{formatCurrency(getDiscountSummary()!.monthlySavings)}</p>
                    </div>
                    <div className="p-4 bg-white rounded-md shadow-sm">
                      <p className="text-sm text-gray-600">Total Savings Over Loan</p>
                      <p className="text-xl font-bold text-green-600">{formatCurrency(getDiscountSummary()!.totalSavings)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Charts and Breakdown */}
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Loan Breakdown</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-md font-semibold text-gray-700 mb-2">Payment Composition</h4>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={getPaymentBreakdownData()}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {getPaymentBreakdownData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => formatCurrency(value as number)} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-md font-semibold text-gray-700 mb-2">Yearly Amortization</h4>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={getAmortizationChartData()}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip formatter={(value) => formatCurrency(value as number)} />
                          <Legend />
                          <Line type="monotone" dataKey="principal" stroke="#3b82f6" name="Principal Paid" />
                          <Line type="monotone" dataKey="interest" stroke="#ef4444" name="Interest Paid" />
                          <Line type="monotone" dataKey="balance" stroke="#10b981" name="Remaining Balance" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>

              {/* Amortization Schedule Table */}
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Amortization Schedule</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Principal</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Interest</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remaining Balance</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {result.amortizationSchedule.map((month) => (
                        <tr key={month.month}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{month.month}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(month.payment)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(month.principal)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(month.interest)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(month.balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Information Section */}
          <div className="mt-8 bg-gray-50 p-6 rounded-md">
            <h3 className="text-lg font-semibold mb-4">Australian Defence Force & Veteran Car Loan Benefits</h3>
            
            <div className="grid md:grid-cols-2 gap-6 mb-4">
              <div className="bg-white p-4 rounded-md shadow-sm">
                <h4 className="flex items-center text-md font-semibold mb-2">
                  <Shield className="h-4 w-4 text-blue-600 mr-2" /> 
                  ADF Active Service Benefits
                </h4>
                <p className="text-sm text-gray-700 mb-2">
                  Active ADF members can access interest rate discounts up to 0.50% off standard rates, plus fee reductions.
                </p>
                <p className="text-xs text-gray-500">
                  Long-serving members (10+ years) may qualify for complete fee waivers
                </p>
              </div>
              
              <div className="bg-white p-4 rounded-md shadow-sm">
                <h4 className="flex items-center text-md font-semibold mb-2">
                  <Medal className="h-4 w-4 text-blue-600 mr-2" /> 
                  Veteran Advantages
                </h4>
                <p className="text-sm text-gray-700 mb-2">
                  Australian veterans receive dedicated rate reductions of 0.35% and simplified approval processes.
                </p>
                <p className="text-xs text-gray-500">
                  Additional benefits for DVA disability pension recipients
                </p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-4 rounded-md shadow-sm">
                <h4 className="flex items-center text-md font-semibold mb-2">
                  <CreditCard className="h-4 w-4 text-blue-600 mr-2" /> 
                  Flexible Financing Options
                </h4>
                <p className="text-sm text-gray-700 mb-2">
                  Choose secured or unsecured loans with competitive rates and military-specific benefits.
                </p>
                <p className="text-xs text-gray-500">
                  Available for new and used vehicles across Australia
                </p>
              </div>
              
              <div className="bg-white p-4 rounded-md shadow-sm">
                <h4 className="flex items-center text-md font-semibold mb-2">
                  <Award className="h-4 w-4 text-blue-600 mr-2" /> 
                  Recognition of Service
                </h4>
                <p className="text-sm text-gray-700 mb-2">
                  Longer service periods earn additional rate discounts and potential fee waivers.
                </p>
                <p className="text-xs text-gray-500">
                  Special consideration for reservists and part-time service
                </p>
              </div>
            </div>

            <div className="mt-4">
              <p className="text-sm text-gray-600">
                Note: This calculator provides estimates based on typical military and veteran benefits offered by Australian lenders. 
                Actual loan terms and military discounts may vary between lenders. Benefits and discounts shown are representative rather 
                than guaranteed. We recommend comparing offers from multiple Australian lenders who specialize in Defence Force financing.
              </p>
            </div>
          </div>
        </div>
      </Calculator>
    </>
  );
};

export default MilitaryVeteranLoanCalculator; 